import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, BookOpen, GraduationCap, TrendingUp, UserCircle, LogOut, HelpCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { HowItWorksDialog } from "@/components/HowItWorks";

const NAV = [
  { to: "/flashcards/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/flashcards/study", label: "Study", icon: BookOpen },
  { to: "/flashcards/courses", label: "Courses", icon: GraduationCap },
  { to: "/flashcards/progress", label: "Progress", icon: TrendingUp },
  { to: "/flashcards/account", label: "Account", icon: UserCircle },
];

export function StudentLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    const key = `ss_guide_seen_${user.id}`;
    if (!localStorage.getItem(key)) {
      setGuideOpen(true);
      localStorage.setItem(key, "1");
    }
  }, [user?.id]);

  const doLogout = async () => {
    setBusy(true);
    await logout();
    navigate("/flashcards");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/science-sorted-logo.png" alt="Science Sorted" className="h-12 w-auto" />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setGuideOpen(true)} data-testid="how-it-works-button"
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors">
              <HelpCircle className="h-4 w-4" /> <span className="hidden sm:inline">How it works</span>
            </button>
            <span className="hidden sm:block text-sm text-slate-500">{user?.name}</span>
            <button onClick={doLogout} disabled={busy} data-testid="logout-button"
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors">
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <HowItWorksDialog open={guideOpen} onOpenChange={setGuideOpen} />

      <div className="hidden md:block sticky top-16 z-20 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 flex gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-desktop-${n.label.toLowerCase()}`}
              className={({ isActive }) => `inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${isActive ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-6 pb-24 md:pb-6">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200">
        <div className="grid grid-cols-5">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2.5 text-[11px] transition-colors ${isActive ? "text-slate-900" : "text-slate-400"}`}>
              <n.icon className="h-5 w-5" />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
